import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  fetchReservas,
  createReserva,
  updateReserva,
  deleteReserva,
  fetchMesas
} from '../services/api.ts';
import { createPublicReserva, listPublicMesas } from '../../../../services/publicReservoApi';
import { getApiErrorMessage } from '../../../../utils/apiError';

export interface Reservation {
  id?: string | number;
  data: string;
  hora: string;
  nome_cliente: string;
  mesa_id: number | string | null;
  mesa_ids?: Array<number | string>;
  mesas?: Array<{
    id?: string | number | null;
    reserva_id?: string | number | null;
    mesa_id: number | string;
    ordem?: number | null;
  }>;
  numero_pessoas: number;
  sala_id: number | string;
  telefone_cliente: string;
  obs?: string;
  email_cliente?: string | null;
  nascimento_cliente?: string | null;
  horario_id?: number | string | null;
  turno?: number | string | null;
  status?: string;
  status_cliente?: string;
  cliente_id?: number | string | null;
  cliente?: {
    id?: number | string;
    nome?: string | null;
    telefone?: string | null;
  } | null;
  capacidade_excedida_confirmada?: boolean;
}

interface Mesa {
  id: number | string;
  numero: number;
  capacidade: number;
  sala_id: number | string;
  posicao_x?: number;
  posicao_y?: number;
  [key: string]: any;
}

interface ReservationContextType {
  reservations: Reservation[];
  loading: boolean;
  createReservation: (reserva: Reservation) => Promise<any>;
  updateReservation: (id: number | string, dados: Partial<Reservation>) => Promise<void>;
  cancelReservation: (id: number | string) => Promise<void>;
  getTableReservations: (mesa_id: number | string) => Reservation[];
  getAvailableTables: (sala_id: string, data: string, hora: string) => Promise<any[]>;
  reload: () => Promise<void>;
}

interface ReservationProviderProps {
  children: React.ReactNode;
  isPublic?: boolean;
  slug?: string;
}

export const ReservationContext = createContext<ReservationContextType | undefined>(undefined);

export const useReservation = (): ReservationContextType => {
  const context = useContext(ReservationContext);
  if (!context) {
    throw new Error('useReservation must be used within a ReservationProvider');
  }
  return context;
};

const normalizeReservation = (reserva: any): Reservation => {
  const cliente = reserva?.cliente || reserva?.reservo_clientes || null;
  const nomeCliente = reserva?.nome_cliente || cliente?.nome || '';
  const telefoneCliente = reserva?.telefone_cliente || cliente?.telefone || '';
  const mesaIds = Array.isArray(reserva?.mesa_ids) && reserva.mesa_ids.length > 0
    ? reserva.mesa_ids.filter((mesaId: any) => mesaId !== null && mesaId !== undefined && mesaId !== '')
    : (reserva?.mesa_id !== null && reserva?.mesa_id !== undefined && reserva?.mesa_id !== ''
      ? [reserva.mesa_id]
      : []);
  return {
    ...reserva,
    cliente,
    cliente_id: reserva?.cliente_id ?? cliente?.id ?? null,
    mesa_id: mesaIds[0] ?? reserva?.mesa_id ?? null,
    mesa_ids: mesaIds,
    nome_cliente: nomeCliente,
    telefone_cliente: telefoneCliente
  };
};

export const ReservationProvider: React.FC<ReservationProviderProps> = ({ children, isPublic = false, slug }) => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);

  const loadReservations = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchReservas();
      const normalized = (data || []).map(normalizeReservation);
      setReservations(normalized);
    } catch (err) {
      console.error('Erro ao carregar reservas:', err);
      if (!silent) {
        toast.error(getApiErrorMessage(err, 'Não foi possível carregar as reservas.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
    // Helpers importados/estáveis; evitar recriar o callback a cada render do provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReservas, normalizeReservation]);

  const createReservation = async (reserva: Reservation) => {
    if (isPublic && slug) {
      return createPublicReserva(slug, reserva);
    }

    const nova = await createReserva(reserva);

    // Reload reservations to ensure dashboard updates with latest data
    await loadReservations();
    return nova;
  };

  const updateReservation = async (id: number | string, dados: Partial<Reservation>) => {
    await updateReserva(id, dados);
    // Reload reservations to ensure dashboard updates with latest data
    await loadReservations();
  };

  const cancelReservation = async (id: number | string) => {
    await deleteReserva(id);
    await loadReservations();
  };

  const getTableReservations = (mesa_id: number | string) => {
    return reservations.filter((r) => (
      String(r.mesa_id) === String(mesa_id)
      || (Array.isArray(r.mesa_ids) && r.mesa_ids.some((currentMesaId) => String(currentMesaId) === String(mesa_id)))
    ));
  };

  const getAvailableTables = async (sala_id: string, data: string, hora: string) => {
    try {
      // 1. Busca todas as mesas da sala
      let todasAsMesas;
      if (isPublic && slug) {
        todasAsMesas = await listPublicMesas(slug);
      } else {
        todasAsMesas = await fetchMesas();
      }
      const mesasDaSala = todasAsMesas.filter((mesa: Mesa) => String(mesa.sala_id) === sala_id);

      // 2. Filtra as reservas da sala para esse dia/hora
      const ocupadas = reservations.filter(
        (r) => String(r.sala_id) === sala_id && r.data && r.data === data && r.hora === hora
      ).flatMap((r) => (
        Array.isArray(r.mesa_ids) && r.mesa_ids.length > 0
          ? r.mesa_ids
          : (r.mesa_id !== null && r.mesa_id !== undefined ? [r.mesa_id] : [])
      ));

      // 3. Retorna as mesas disponíveis
      const disponiveis = mesasDaSala.filter((mesa: Mesa) => !ocupadas.includes(mesa.id));

      return disponiveis;
    } catch (err) {
      console.error('Erro ao buscar mesas disponíveis:', err);
      toast.error(getApiErrorMessage(err, 'Não foi possível carregar as mesas disponíveis.'));
      return [];
    }
  };

  useEffect(() => {
    if (!isPublic) {
      loadReservations();
      // Poll for updates every 30 seconds to keep dashboard always updated
      const interval = setInterval(() => {
        loadReservations({ silent: true });
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isPublic, loadReservations]);

  return (
    <ReservationContext.Provider
      value={{
        reservations,
        loading,
        createReservation,
        updateReservation,
        cancelReservation,
        getTableReservations,
        getAvailableTables,
        reload: loadReservations,
      }}
    >
      {children}
    </ReservationContext.Provider>
  );
};
